import { z } from 'zod';

import { Id, IsoDateTime, Paise } from '../common/primitives';

export { Paise } from '../common/primitives';

// ── Enums ──────────────────────────────────────────────────────────────────

export const AnalyticsDomainSchema = z.enum(['CUSTOMER', 'VENDOR', 'FINANCE', 'OPS', 'PLATFORM']);
export type AnalyticsDomain = z.infer<typeof AnalyticsDomainSchema>;

export const AnalyticsPeriodSchema = z.enum(['DAY', 'WEEK', 'MONTH', 'YEAR']);
export type AnalyticsPeriod = z.infer<typeof AnalyticsPeriodSchema>;

// ── Metric shapes ──────────────────────────────────────────────────────────

export const CustomerMetricsSchema = z.object({
  userId: Id,
  orderCount: z.number().int().nonnegative(),
  lifetimeValuePaise: Paise,
  avgOrderValuePaise: Paise,
  repeatOrders: z.number().int().nonnegative(),
  daysSinceLastOrder: z.number().int().nullable(),
});
export type CustomerMetrics = z.infer<typeof CustomerMetricsSchema>;

export const VendorMetricsSchema = z.object({
  vendorId: Id,
  gmvPaise: Paise,
  orderCount: z.number().int().nonnegative(),
  deliveredCount: z.number().int().nonnegative(),
  returnedCount: z.number().int().nonnegative(),
  /// Returned ÷ delivered, in basis points (10000 = 100%).
  returnRateBps: z.number().int().nonnegative(),
  /// Delivered ÷ confirmed, in basis points.
  fulfillmentRateBps: z.number().int().nonnegative(),
  avgOrderValuePaise: Paise,
});
export type VendorMetrics = z.infer<typeof VendorMetricsSchema>;

export const FinanceMetricsSchema = z.object({
  revenuePaise: Paise,
  cogsPaise: Paise,
  operatingExpensePaise: Paise,
  /// revenue − cogs − opex.
  netPaise: z.number().int(),
  /// (revenue − cogs) ÷ revenue, basis points.
  grossMarginBps: z.number().int(),
  /// net ÷ revenue, basis points.
  netMarginBps: z.number().int(),
  /// Monthly run-rate based on opex over the period.
  burnRatePaise: Paise,
});
export type FinanceMetrics = z.infer<typeof FinanceMetricsSchema>;

export const OpsMetricsSchema = z.object({
  /// Total delivered sub-orders in the period.
  deliveredCount: z.number().int().nonnegative(),
  /// Sub-orders delivered within their return window — proxy for on-time delivery.
  onTimeCount: z.number().int().nonnegative(),
  /// onTime ÷ delivered, basis points.
  slaBps: z.number().int().nonnegative(),
  /// Sum of `Settlement.grossPaise` for settled rows.
  settledGmvPaise: Paise,
  /// Total inventory value across InternalWarehouses (paise).
  inventoryValuePaise: Paise,
  /// Damage write-off in the period (paise).
  damagePaise: Paise,
});
export type OpsMetrics = z.infer<typeof OpsMetricsSchema>;

export const PlatformMetricsSchema = z.object({
  gmvPaise: Paise,
  orderCount: z.number().int().nonnegative(),
  uniqueCustomerCount: z.number().int().nonnegative(),
  posSalesPaise: Paise,
  b2bSalesPaise: Paise,
  marketplaceSalesPaise: Paise,
  saasRevenuePaise: Paise,
});
export type PlatformMetrics = z.infer<typeof PlatformMetricsSchema>;

export const AnalyticsSnapshotSchema = z.object({
  id: Id,
  domain: AnalyticsDomainSchema,
  period: AnalyticsPeriodSchema,
  periodStart: IsoDateTime,
  periodEnd: IsoDateTime,
  scopeId: z.string().nullable(),
  metrics: z.record(z.number()),
  computedAt: IsoDateTime,
});
export type AnalyticsSnapshot = z.infer<typeof AnalyticsSnapshotSchema>;

export const DashboardSchema = z.object({
  generatedAt: IsoDateTime,
  platform: PlatformMetricsSchema,
  finance: FinanceMetricsSchema,
  ops: OpsMetricsSchema,
});
export type Dashboard = z.infer<typeof DashboardSchema>;

// ── Request shapes ────────────────────────────────────────────────────────

export const PeriodQuery = z
  .object({
    period: AnalyticsPeriodSchema.default('MONTH'),
    /// ISO date marking the start of the period. Defaults to the current period boundary.
    periodStart: IsoDateTime.optional(),
  })
  .strict();
export type PeriodQuery = z.infer<typeof PeriodQuery>;

export const RunSnapshotRequest = z
  .object({
    period: AnalyticsPeriodSchema.default('DAY'),
    /// Reference date; defaults to today UTC. Used for backfills.
    asOf: IsoDateTime.optional(),
  })
  .strict();
export type RunSnapshotRequest = z.infer<typeof RunSnapshotRequest>;

export const SnapshotRunResultSchema = z.object({
  period: AnalyticsPeriodSchema,
  periodStart: IsoDateTime,
  periodEnd: IsoDateTime,
  snapshotsWritten: z.number().int().nonnegative(),
});
export type SnapshotRunResult = z.infer<typeof SnapshotRunResultSchema>;

export const SystemHealthSchema = z.object({
  apiServer: z.string(),
  database: z.string(),
  redisCache: z.string(),
  workerQueue: z.string(),
});
export type SystemHealth = z.infer<typeof SystemHealthSchema>;

export const LiveActivitySchema = z.object({
  id: Id,
  title: z.string(),
  desc: z.string(),
  time: IsoDateTime,
  type: z.string(),
});
export type LiveActivity = z.infer<typeof LiveActivitySchema>;
